import React from 'react';
import PropTypes from 'prop-types';

import {Box, Card, TextField, Stack, Button} from 'views/components';
import {CaregiverMap, Translate, translate} from 'views/module';

const MapFilter = (props) => {
  const {caregivers, onBoundsChange, onMarkerClick, jobLocation} = props;

  const [center, setCenter] = React.useState([45.4215, -75.6972]); // Default to Ottawa
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searching, setSearching] = React.useState(false);
  const [shouldRecenter, setShouldRecenter] = React.useState(false);
  // Store both bounds and zoom from map
  const [mapState, setMapState] = React.useState({bounds: null, zoom: null});
  const [hasUserLocation, setHasUserLocation] = React.useState(false);
  // Track initial bounding zone and zoom
  const initialZoneRef = React.useRef(null);
  const initialZoomRef = React.useRef(null);
  const lastZoomRef = React.useRef(null);

  // Set initial center and search query from jobLocation
  React.useEffect(() => {
    if (jobLocation) {
      const locationString = [jobLocation.neighborhood, jobLocation.city]
          .filter(Boolean)
          .join(', ');

      if (locationString) {
        setSearchQuery(locationString);

        // Geocode the location
        const geocodeLocation = async () => {
          try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationString)}`,
            );
            const data = await res.json();
            if (data.length > 0) {
              const {lat, lon} = data[0];
              setCenter([parseFloat(lat), parseFloat(lon)]);
              setShouldRecenter(true);
              setHasUserLocation(true);
            }
          } catch (err) {
            console.error('Geocoding error:', err);
          }
        };

        geocodeLocation();
      }
    }
  }, [jobLocation]);

  const handleSearchSubmit = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);

    try {
      const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`,
      );
      const data = await res.json();
      if (data.length > 0) {
        const {lat, lon} = data[0];
        setCenter([parseFloat(lat), parseFloat(lon)]);
        setShouldRecenter(true);
        setHasUserLocation(true);
      }
    } catch (err) {
      console.error('Geocoding error:', err);
    } finally {
      setSearching(false);
    }
  };

  // Reset recenter flag after it's been used
  React.useEffect(() => {
    if (shouldRecenter) {
      const timer = setTimeout(() => setShouldRecenter(false), 100);
      return () => clearTimeout(timer);
    }
  }, [shouldRecenter]);

  // If the address/search field is cleared, hide the center marker again
  React.useEffect(() => {
    if (!searchQuery.trim()) {
      setHasUserLocation(false);
    }
  }, [searchQuery]);

  // Setting radius center when user clicks on map
  const handleLocationClick = async (latlng) => {
    setCenter([latlng.lat, latlng.lng]);
    setHasUserLocation(true);

    // Reverse geocode to get address
    try {
      const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}`,
      );
      const data = await res.json();
      if (data.display_name) {
        setSearchQuery(data.display_name);
      }
    } catch (err) {
      console.error('Reverse geocoding error:', err);
    }
  };

  const visibleCaregivers = React.useMemo(() => {
    if (!caregivers?.length) return [];
    return caregivers;
  }, [caregivers]);

  // Handle marker click - both highlight and select
  const handleMarkerClick = React.useCallback((caregiverId) => {
    onMarkerClick(caregiverId);
  }, [onMarkerClick]);

  // Main effect: handle bounds and zoom logic
  React.useEffect(() => {
    const {bounds, zoom} = mapState;
    // Inline buffer bounds calculation
    let bufferBounds = null;
    if (bounds && zoom != null) {
      let buffer = 0.2;
      if (zoom >= 10) buffer = 0.04;
      else if (zoom >= 8) buffer = 0.1;
      const {_southWest, _northEast} = bounds;
      bufferBounds = {
        minLat: _southWest.lat - buffer,
        minLng: _southWest.lng - buffer,
        maxLat: _northEast.lat + buffer,
        maxLng: _northEast.lng + buffer,
      };
    }

    // On first load, set initial zone and zoom
    if (!initialZoneRef.current) {
      initialZoneRef.current = bufferBounds;
      initialZoomRef.current = zoom;
      lastZoomRef.current = zoom;
      onBoundsChange(bufferBounds);
      return;
    }

    // Bounds comparison: if bounds leave the initial zone, call backend and update zone
    const {_southWest, _northEast} = bounds;
    const zone = initialZoneRef.current;
    if (
      _southWest.lat < zone.minLat ||
      _southWest.lng < zone.minLng ||
      _northEast.lat > zone.maxLat ||
      _northEast.lng > zone.maxLng
    ) {
      initialZoneRef.current = bufferBounds;
      lastZoomRef.current = zoom;
      onBoundsChange(bufferBounds);
      return;
    }

    // If zoomed in two or more levels above last backend-triggering zoom, call backend and update zone
    if (zoom >= lastZoomRef.current + 2) {
      initialZoneRef.current = bufferBounds;
      lastZoomRef.current = zoom;
      onBoundsChange(bufferBounds);
      return;
    }
  }, [mapState, onBoundsChange]);

  return (
    <Card
      sx={{height: '600px', display: 'flex', flexDirection: 'column', borderRadius: '14px', overflow: 'hidden'}}
    >
      <Box sx={{p: 2}}>
        <Stack spacing={2}>
          {/* Search Box */}
          <Box component="form" onSubmit={(e) => {
            e.preventDefault();
            handleSearchSubmit();
          }}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={translate('caregiver.CaregiverList.Map.SearchPlaceholder')}
                disabled={searching}
                type="search"
              />
              <Button type="submit" variant="contained" disabled={searching}>
                <Translate expression={'caregiver.CaregiverList.Map.Search'} />
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>
      {/* Map fills remaining space */}
      <Box sx={{flexGrow: 1}}>
        <CaregiverMap
          height="100%"
          width="100%"
          coordinates={center}
          caregivers={visibleCaregivers}
          onLocationClick={handleLocationClick}
          onBoundsChange={(payload) => {
            setMapState(payload);
          }}
          onMarkerClick={handleMarkerClick}
          shouldRecenter={shouldRecenter}
          showCenterMarker={hasUserLocation && Boolean(searchQuery.trim())}
          zoom={13}
        />
      </Box>
    </Card>
  );
};

MapFilter.propTypes = {
  caregivers: PropTypes.array,
  onBoundsChange: PropTypes.func,
  onMarkerClick: PropTypes.func,
  jobLocation: PropTypes.object,
};

export default React.memo(MapFilter);
